import { HtmlParser } from '@calendly/html-parser';
import { FullStoryMask } from '@calendly/ui/components/fullstory-privacy';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import React from 'react';
import { StepThree } from 'features/meeting_intelligence';
import { useHasSpotlight } from 'features/meetings/hooks';
import type { Meeting } from 'features/meetings/types';
import { useMeetingSummaryData } from './useMeetingSummaryData';
const containerCss = "c144gbtt";
const headerCss = "hhlt4mz";
const titleCancelledCss = "twpmob6";
const titleCss = "t1pl65mj";
const subtitleCss = "sqahmye";
const subtitleTextCss = "s105vn3w";
const separatorCss = "sq0g36t";
interface MeetingSummaryProps {
  event: Meeting;
  packageName?: string | null;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Renders the title and participant summary section of a meeting card.
 *
 * Displays the event name, optional host prefix, invitee/group participant text, and an
 * optional package name. Applies a strikethrough to the title when the
 * meeting is cancelled. Integrates with the Meeting Intelligence onboarding
 * tour step-three spotlight on desktop.
 *
 * @param event - The meeting whose name and participant data are displayed
 * @param packageName - Optional package booking name rendered below the title
 * @param className - Optional additional CSS class for the container
 * @param children - Optional content rendered after the summary (e.g. warning badges)
 */
export const MeetingSummary = ({
  event,
  packageName,
  className,
  children
}: MeetingSummaryProps) => {
  const hasSpotlight = useHasSpotlight(event.key);
  const {
    cancelled,
    isGroup,
    hostName,
    participantText,
    inviteeText,
    groupText,
    showStepThree,
    isPhone
  } = useMeetingSummaryData(event);
  let hostSubtitlePrefix: React.ReactNode = null;
  if (hostName) {
    hostSubtitlePrefix = <FullStoryMask>
        <span className={separatorCss}>- </span>
        {hostName}
        {' '}
      </FullStoryMask>;
  }
  return <div className={classNames(containerCss, className)}>
      <div className={headerCss}>
        <span className={classNames(titleCss, {
        [titleCancelledCss]: cancelled
      })}>
          {event.name}
        </span>
        <span className={subtitleCss}>
          {showStepThree && hasSpotlight && !isPhone && <StepThree isVisible={hasSpotlight} />}
          <span className={subtitleTextCss}>
            {participantText ? <FullStoryMask>
                {HtmlParser(participantText)}
              </FullStoryMask> : <>
                {hostSubtitlePrefix}
                {!isGroup && <FullStoryMask>
                    {inviteeText}
                  </FullStoryMask>}
                {isGroup && groupText}
              </>}
          </span>
        </span>
      </div>
      {packageName && !isGroup && <BodyText color="muted" size="small">
          {packageName}
        </BodyText>}
      {children}
    </div>;
};

require("./MeetingSummary.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./MeetingSummary.tsx");